import {
  isLatitudeType,
  isLongitudeType,
  type MessagePathNode,
  CanonicalDataType,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";
import { type PanelState, PanelType } from "@/shared/state/visualization";

export function isViewableAsImage(messagePath: MessagePathNode) {
  return messagePath.data.canonical_data_type === CanonicalDataType.Image;
}

export function isViewableAsPlot(messagePath: MessagePathNode) {
  return (
    messagePath.isLeafNode() &&
    (isLatitudeType(messagePath.data.canonical_data_type) ||
      isLongitudeType(messagePath.data.canonical_data_type) ||
      [
        CanonicalDataType.Number,
        CanonicalDataType.Boolean,
        CanonicalDataType.Timestamp,
        // The PlotPanel will attempt pick values out of arrays and coerce them to numbers.
        // If it is unable to coerce a value to a number, it will raise an exception.
        CanonicalDataType.Array,
        CanonicalDataType.NumberArray,
        CanonicalDataType.Categorical,
        CanonicalDataType.String,
      ].includes(messagePath.data.canonical_data_type))
  );
}

export function isViewableAsThreeD() {
  // TODO(ENG-1672): Support drag & drop topic data in the ThreeDPanel.
  return false;
}

export function isViewableAsMap(messagePath: MessagePathNode) {
  // Note(ENG-2040): Prevent numerical types to be viewed as map
  // until we have a good UX to assign paths to lat/lon slots.
  if (messagePath.data.canonical_data_type === CanonicalDataType.Number) {
    return false;
  }

  return (
    isLatitudeType(messagePath.data.canonical_data_type) ||
    isLongitudeType(messagePath.data.canonical_data_type)
  );
}

export function isViewableAsRawMessage(messagePath: MessagePathNode): boolean {
  return (
    messagePath.hasRepresentation(RepresentationStorageFormat.MCAP) ||
    messagePath.hasRepresentation(RepresentationStorageFormat.PARQUET)
  );
}

export function isViewableAsConsoleLog(messagePath: MessagePathNode) {
  // A log column renders via the MCAP/Parquet readers, so it needs a readable
  // representation; a path with none can't be shown, and toTopicData() would
  // throw if such a path reached the add path.
  return (
    !isViewableAsImage(messagePath) &&
    (messagePath.hasRepresentation(RepresentationStorageFormat.MCAP) ||
      messagePath.hasRepresentation(RepresentationStorageFormat.PARQUET))
  );
}

export function panelAcceptsMessagePath(
  panel: PanelState,
  messagePath: MessagePathNode,
): boolean {
  const panelType = panel.type;
  switch (panelType) {
    case PanelType.Plot:
      return isViewableAsPlot(messagePath);
    case PanelType.ThreeD:
      return isViewableAsThreeD();
    case PanelType.RawMessage:
      return isViewableAsRawMessage(messagePath);
    case PanelType.Image:
      return isViewableAsImage(messagePath);
    case PanelType.Map:
      return isViewableAsMap(messagePath);
    case PanelType.Log:
      return isViewableAsConsoleLog(messagePath);
    default:
      return false;
  }
}
